import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Album,
  Cart,
  FilterSelections,
  Photo,
  ThumbnailSelectionMode
} from "./types";
import AlbumPhotos from "./AlbumPhotos";
import ajax from "superagent";
import { EventGalleryContext } from "./index";
import AlbumsList from "./AlbumsList";
import Photos from "./Photos";
import NoMatchingPhotos from "./Filters/NoMatchingPhotos";
import _ from "lodash";
import { removeParam, addParam } from "../../utils/BrowserHelper";
const gssNewLoader = require("../../assets/gss-loader.gif");
import Telemetry from "../../utils/rudderstackAnalytics";
import SaveCart from "../SaveCartEmailPopup";
import Photos2 from "./Photos2";

interface Like {
  photoId: number;
  userId: number;
}

const GalleryBody = ({
  albums,
  eventPhotosAPI,
  openedAlbum,
  userInputPassword,
  listOfMediaId,
  userId,
  setGuestUserCart,
  handlePhotoAddedToCart,
  photoAddedToCart,
  removePhotoAddedToCart
}: {
  albums: Album[];
  eventPhotosAPI: string;
  openedAlbum: Album | null;
  userInputPassword: string;
  listOfMediaId: Array<{ id: number; hidden: boolean }>;
  userId: number | null;
  setGuestUserCart: (userId: number, userEmail: string, cart: Cart) => void;
  handlePhotoAddedToCart: (photo: any) => void;
  photoAddedToCart: any;
  removePhotoAddedToCart: () => void;
}) => {
  const [activeAlbum, toggleAlbum] = useState<Album | null>(openedAlbum);
  const [filteredPhotos, setFilteredPhotos] = useState<Photo[]>([]);
  const [nextPage, setNextPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showSaveCartModal, setShowSaveCartModal] = useState(null);
  const context = useContext(EventGalleryContext);

  const mounted = useRef(true);

  const photosRequest = (
    url: string,
    filters: FilterSelections,
    pageNumber: number,
    userIdProp?: number
  ) => {
    let req = ajax.get(url).query({
      page: pageNumber,
      user_id: userId || userIdProp || context.guestUserId,
      cartId: context.cart ? context.cart.id : null
    });

    if (userInputPassword !== "") {
      req = req.query({
        event_password: userInputPassword
      });
    }

    if (filters.photoTime.active) {
      req = req.query({
        minute_from: filters.photoTime.minuteFrom,
        minute_to: filters.photoTime.minuteTo
      });
    }

    if (filters.referenceImage) {
      req = req.query({
        reference_image: filters.referenceImage
      });
    }

    if (filters.albums.length > 0) {
      const albumIdsString = filters.albums
        .map(albumId => `album_ids[]=${albumId}`)
        .join("&");
      req = req.query(albumIdsString);
    }

    if (filters.photoText) {
      req = req.query({
        photo_text: filters.photoText
      });
    }

    if (filters.favorite) {
      req = req.query({
        favorite: true
      });
    }

    if (filters.photographers) {
      req = req.query({
        "album_ids[]": _.map(
          _.filter(
            albums,
            album =>
              //@ts-ignore
              _.indexOf(filters.photographers, album.photographerId) >= 0
          ),
          "id"
        )
      });
    }

    return req;
  };

  const trackSelfieSearchResults = (isResultsFound: boolean) => {
    if (context.filterSelections.referenceImage) {
      Telemetry.track("event-gallery:selfie-search-completed", {
        photosAvailable: isResultsFound
      });
    }
  };

  const loadPhotos = async (
    filters: FilterSelections,
    reset: boolean = false,
    userIdProp?: number
  ) => {
    context.updatePhotosCount(0);
    try {
      const response = await photosRequest(
        eventPhotosAPI,
        filters,
        reset ? 1 : nextPage,
        userIdProp
      );

      await new Promise<void>(resolve => {
        setFilteredPhotos(previousPhotos => {
          if (reset || context.reset) {
            context.setPhotos(galleryPhotos([...response.body.photos]));
            return galleryPhotos([...response.body.photos]);
          } else {
            context.setPhotos(
              galleryPhotos([...previousPhotos, ...response.body.photos])
            );
            return galleryPhotos([...previousPhotos, ...response.body.photos]);
          }
        });
        resolve();
      });

      setNextPage(response.body.meta.nextPage);
      context.updatePhotosCount(response.body.meta.totalCount);

      if (response.body.meta.currentPage == 1) {
        if (response.body.meta.totalCount) {
          trackSelfieSearchResults(true);
        } else {
          trackSelfieSearchResults(false);
        }
      }

      context.reloadAlbumPhotos(false);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const albumIdOpen = urlParams.get("album_id_open");

    if (context.activeFilters.length > 0 && !photoAddedToCart) {
      setLoading(true);
      loadPhotos(context.filterSelections, true);
    } else {
      context.activeFilters.length == 0 && setFilteredPhotos([]);

      // Prevent clearing album if album_id_open exists in the URL
      if (!albumIdOpen) {
        // This is to make sure user doesn't get confused that
        // filters are specific to album
        if (!mounted.current) toggleAlbum(null);
        else mounted.current = false;
      }

      setLoading(false);
    }
  }, [context.filterSelections]);

  useEffect(() => {
    if (context.reset === true) {
      setLoading(true);
      loadPhotos(context.filterSelections, true);
    }
  }, [context.activeFilters.length > 0 ? context.reset : 0]);

  useEffect(() => {
    if (context.activeFilters.length > 0) {
      setFilteredPhotos(galleryPhotos(filteredPhotos));
    }
  }, [listOfMediaId.length, context.cart ? context.cart.photos.length : 0]);

  const noPhotosMatched =
    context.activeFilters.length > 0 && filteredPhotos.length === 0;

  const setAlbum = (album: Album | null) => {
    toggleAlbum(album);
    if (album) addParam("album_id_open", album.signedId);
    else removeParam("album_id_open", window.location.href);
  };

  const shouldShowDownload = () =>
    context.event.price === 0 && context.directDownloadEnabled;

  const isSelected = (photo: Photo) =>
    _.includes(
      context.curationMode || shouldShowDownload()
        ? context.listOfMediaId.map(item => item.id)
        : context.cart
        ? context.cart.photos
        : [],
      photo.id
    );
  const prepaidAuthCookie = () =>
    context.event.prepaidCustomersPrimaryAuthField === "cookie";

  const canShowGuestEmailModal = (photo: any) => {
    if (context.canShowEmailPopup()) {
      setShowSaveCartModal(photo);
      handlePhotoAddedToCart(photo);
      return true;
    } else return false;
  };

  const galleryPhotos = (photos: any) => {
    return _.uniqBy(photos, "id").map((photo: any) => {
      return {
        ...photo,
        ...(photo.timeTaken
          ? {
              tags: [
                {
                  title: "Time",
                  value: photo.timeTaken
                }
              ]
            }
          : {}),
        isSelected: isSelected(photo),
        src: "",
        disableContextMenu: true
      };
    });
  };
  return (
    <div className="mobile-photos-ht">
      {loading ? (
        <div className="egm-loader gss-loader" style={{ clear: "both" }}>
          {" "}
          <img className="photos-date-img" src={gssNewLoader} />
        </div>
      ) : noPhotosMatched ? (
        <NoMatchingPhotos />
      ) : filteredPhotos.length > 0 ? (
        <div>
          {window.gss?.albumV2Enabled && !context.curationMode ? (
            <Photos2
              photos={filteredPhotos}
              windowingProps={{
                hasMore: !!nextPage,
                isLoading: loading,
                loadMore: () => loadPhotos(context.filterSelections)
              }}
              selectionMode={ThumbnailSelectionMode.CART}
              isMobile={true}
              event={context.event}
              album={activeAlbum}
              trackTelemetry={true}
              userId={context.userId ? context.userId : undefined}
              isAuthorizedForDirectDownloads={false}
              parentName={"event-gallery-lightbox"}
              showFavIcon={true}
              canShowGuestEmailModal={canShowGuestEmailModal}
            />
          ) : (
            <Photos
              photos={filteredPhotos}
              loadPhotos={() => loadPhotos(context.filterSelections)}
              hasMore={!!nextPage}
              trackTelemetry={true}
              userId={context.userId ? context.userId : undefined}
              isAuthorizedForDirectDownloads={false}
              reset={context.filterSelections.favorite ? true : false}
              showFavIcon={true}
              canShowGuestEmailModal={canShowGuestEmailModal}
              registerGuestUserAPI={context.registerGuestUserAPI}
              setGuestUserId={context.setGuestUserId}
              eventClickable={context.eventClickable}
            />
          )}
        </div>
      ) : activeAlbum ? (
        <AlbumPhotos
          album={activeAlbum}
          toggleAlbum={setAlbum}
          eventPassword={userInputPassword}
          canShowGuestEmailModal={canShowGuestEmailModal}
        />
      ) : (
        <AlbumsList albums={albums} toggleAlbum={setAlbum} />
      )}
      {showSaveCartModal && (
        <SaveCart
          closeSaveCartModal={(shouldAddPhoto = true) => {
            removePhotoAddedToCart();
            setShowSaveCartModal(null);
            if (shouldAddPhoto && !prepaidAuthCookie()) {
              context.addPhotoToCart(showSaveCartModal);
            }
          }}
          setSaveCartPromptShown={() =>
            localStorage.setItem("isUserAskedForEmailToSaveCart", "true")
          }
          setGuestUserCart={context.setGuestUserCart}
          trackCartEmailPopupActions={context.trackCartEmailPopupActions}
          emailMandatory={prepaidAuthCookie()}
          onNotNow={() => {
            setShowSaveCartModal(null);
          }}
        />
      )}
    </div>
  );
};
export default GalleryBody;
